import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyRewards, redeemReward, GetMyRewardsOutputType } from 'zite-endpoints-sdk';
import { useFeatureToggles } from '../components/FeatureToggleProvider';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Zap, Star, Brain, ShoppingBag, Clock, CheckCircle2, XCircle, ExternalLink, Gift, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type RewardsData = GetMyRewardsOutputType;
type RewardItem = RewardsData['catalog'][0];
type Attempt = RewardsData['attempts'][0];
type Redemption = RewardsData['redemptions'][0];

const statusColor: Record<string, string> = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Processing: 'bg-blue-100 text-blue-800',
  Fulfilled: 'bg-green-100 text-green-800',
  Cancelled: 'bg-muted text-muted-foreground',
};

function fmtDate(d?: string) {
  if (!d) return '';
  try { return format(parseISO(d), 'd MMM yyyy'); } catch { return ''; }
}

function AttemptReview({ attempt }: { attempt: Attempt }) {
  const [open, setOpen] = useState(false);

  let questions: Array<{ id: string; question: string; options: string[]; correct: number | number[]; explanation: string }> = [];
  let answers: Array<{ id: string; answer: number | number[] }> = [];
  try {
    if (attempt.questionsJson) questions = JSON.parse(attempt.questionsJson);
    if (attempt.answersJson) answers = JSON.parse(attempt.answersJson);
  } catch { /* ignore */ }

  const answerMap = new Map(answers.map(a => [a.id, a.answer]));

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${attempt.percentage >= 80 ? 'bg-green-100' : attempt.percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'}`}>
          <Brain className={`w-4 h-4 ${attempt.percentage >= 80 ? 'text-green-600' : attempt.percentage >= 50 ? 'text-yellow-600' : 'text-red-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{attempt.sectionTitle}</p>
          <p className="text-xs text-muted-foreground">{fmtDate(attempt.completedAt)} · {attempt.score}/{attempt.totalQuestions} correct · {attempt.percentage}%</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-primary">+{attempt.pointsEarned} pts</span>
          {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {open && questions.length > 0 && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
          {questions.map((q, i) => {
            const userAns = answerMap.get(q.id);
            const userArr = Array.isArray(userAns) ? userAns : userAns !== undefined ? [userAns as number] : [];
            const correctArr = Array.isArray(q.correct) ? q.correct : [q.correct as number];
            const isCorrect = JSON.stringify([...userArr].sort()) === JSON.stringify([...correctArr].sort());
            return (
              <div key={q.id} className={`rounded-lg p-3 text-xs ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start gap-2 mb-2">
                  {isCorrect ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />}
                  <p className="font-medium text-foreground">Q{i + 1}: {q.question}</p>
                </div>
                <div className="ml-5 space-y-0.5">
                  {q.options.map((opt, oi) => {
                    const wasSelected = userArr.includes(oi);
                    const isRight = correctArr.includes(oi);
                    if (!wasSelected && !isRight) return null;
                    return (
                      <p key={oi} className={isRight ? 'text-green-700 font-medium' : 'text-red-600'}>
                        {isRight ? '✓' : '✗'} {opt} {wasSelected && !isRight ? '(your answer)' : isRight && !wasSelected ? '(correct answer)' : ''}
                      </p>
                    );
                  })}
                </div>
                {q.explanation && <p className="ml-5 mt-1.5 text-muted-foreground italic">💡 {q.explanation}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MyRewards() {
  const { user } = useAuth();
  const [data, setData] = useState<RewardsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState<string | null>(null);

  const load = () => {
    getMyRewards({}).then(setData).catch(() => toast.error('Failed to load rewards')).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const handleRedeem = async (item: RewardItem) => {
    setRedeeming(item.id);
    try {
      const res = await redeemReward({ rewardItemId: item.id });
      toast.success(`Redeemed! Your new balance: ${res.newBalance} pts`);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Redemption failed');
    } finally {
      setRedeeming(null);
    }
  };

  if (loading) return (
    <div className="px-6 py-8 max-w-3xl mx-auto space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  const { isEnabled } = useFeatureToggles();
  const showQuizHistory = isEnabled('quiz-history');
  const pts = data?.pointsBalance ?? 0;

  return (
    <div className="px-4 md:px-6 py-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" /> My Rewards & Learning
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Earn points by taking mini quizzes and redeem them for vouchers</p>
      </div>

      {/* Points balance */}
      <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-5 text-primary-foreground mb-6 relative overflow-hidden">
        <div className="relative z-10">
          <p className="text-sm opacity-80 font-medium mb-1">Your Points Balance</p>
          <p className="text-5xl font-bold">{pts.toLocaleString()}</p>
          <p className="text-sm opacity-70 mt-1">points</p>
          <div className="flex flex-wrap gap-3 mt-4 text-xs opacity-80">
            <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> 5 pts · participation</span>
            <span className="flex items-center gap-1"><Star className="w-3 h-3" /> 10 pts · per correct answer</span>
            <span className="flex items-center gap-1"><Trophy className="w-3 h-3" /> +20 pts · perfect score</span>
          </div>
        </div>
        <Trophy className="absolute right-4 bottom-0 w-24 h-24 opacity-10" />
      </div>

      <Tabs defaultValue="catalog">
        <TabsList className="mb-4">
          <TabsTrigger value="catalog"><Gift className="w-3.5 h-3.5 mr-1.5" /> Rewards Shop</TabsTrigger>
          {showQuizHistory && <TabsTrigger value="history"><Brain className="w-3.5 h-3.5 mr-1.5" /> Quiz History ({data?.attempts.length ?? 0})</TabsTrigger>}
          <TabsTrigger value="redemptions"><ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> My Orders ({data?.redemptions.length ?? 0})</TabsTrigger>
        </TabsList>

        {/* Rewards Shop */}
        <TabsContent value="catalog">
          {(!data?.catalog || data.catalog.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground">
              <Gift className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No rewards available yet</p>
              <p className="text-sm">Check back soon — rewards will be added here!</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.catalog.map(item => {
                const canAfford = pts >= item.pointsCost;
                return (
                  <div key={item.id} className={`border rounded-xl bg-card overflow-hidden transition-shadow hover:shadow-md ${!canAfford ? 'opacity-70' : ''}`}>
                    {item.imageUrl && (
                      <div className="h-28 bg-muted overflow-hidden">
                        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-semibold text-foreground">{item.name}</p>
                        {item.type && <Badge variant="outline" className="text-xs shrink-0">{item.type}</Badge>}
                      </div>
                      {item.description && <p className="text-xs text-muted-foreground mb-3">{item.description}</p>}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Zap className="w-4 h-4 text-primary" />
                          <span className="font-bold text-foreground">{item.pointsCost.toLocaleString()}</span>
                          <span className="text-xs text-muted-foreground">points</span>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" disabled={!canAfford || redeeming === item.id}>
                              {canAfford ? 'Redeem' : `Need ${(item.pointsCost - pts).toLocaleString()} more pts`}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Redeem "{item.name}"?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <p className="text-sm text-muted-foreground px-6">
                              This will use <strong>{item.pointsCost} points</strong> from your balance of <strong>{pts} points</strong>. A redemption request will be sent to your manager who will arrange fulfilment.
                            </p>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleRedeem(item)}>Confirm Redemption</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Quiz History */}
        <TabsContent value="history">
          {(!data?.attempts || data.attempts.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground">
              <Brain className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No quizzes taken yet</p>
              <p className="text-sm">Take mini quizzes on handbook sections to earn points!</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-3">Click a quiz to review your answers and see explanations</p>
              {data.attempts.map(attempt => <AttemptReview key={attempt.id} attempt={attempt} />)}
            </div>
          )}
        </TabsContent>

        {/* My Redemptions */}
        <TabsContent value="redemptions">
          {(!data?.redemptions || data.redemptions.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No redemptions yet</p>
              <p className="text-sm">Earn points and redeem them for rewards in the shop!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.redemptions.map(r => (
                <div key={r.id} className="border border-border rounded-xl bg-card p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-semibold text-foreground">{r.rewardName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Requested {fmtDate(r.requestedAt)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[r.status || 'Pending'] || 'bg-muted text-muted-foreground'}`}>
                        {r.status || 'Pending'}
                      </span>
                      <span className="text-xs text-muted-foreground">{r.pointsSpent} pts</span>
                    </div>
                  </div>

                  {r.status === 'Fulfilled' && r.voucherDetails && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                      <p className="text-xs font-semibold text-green-800 mb-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Your Voucher Details
                      </p>
                      <p className="text-xs text-green-700 whitespace-pre-wrap">{r.voucherDetails}</p>
                    </div>
                  )}

                  {r.balanceCheckUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 gap-1.5 text-xs"
                      onClick={() => window.open(r.balanceCheckUrl!, '_blank')}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Check Balance
                    </Button>
                  )}

                  {r.status === 'Fulfilled' && r.fulfilledAt && (
                    <p className="text-xs text-muted-foreground mt-2">Fulfilled {fmtDate(r.fulfilledAt)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
